import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';
import { 
  Mail, Calendar, FolderOpen, FileSpreadsheet, FileText, 
  CheckCircle, XCircle, ExternalLink, RefreshCw, Unlink,
  AlertTriangle, Loader2, Settings, Eye, EyeOff, Save, Key
} from 'lucide-react';
import * as api from '../services/api';

const GoogleIntegrationPanel = ({ currentUser, isAdmin }) => {
  const [googleConfig, setGoogleConfig] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [error, setError] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [configForm, setConfigForm] = useState({ client_id: '', client_secret: '' });
  const [showSecret, setShowSecret] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, statusRes] = await Promise.all([
        api.getGoogleConfig(),
        api.getGoogleStatus(currentUser.id)
      ]);
      setGoogleConfig(configRes.data);
      setConnectionStatus(statusRes.data);
      setError(null);
    } catch (err) {
      console.error('Error loading Google status:', err);
      setError('Error cargando estado de Google');
    } finally {
      setLoading(false);
    }
  }, [currentUser.id]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Handle OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state) {
      handleOAuthCallback(code, state);
    }
  }, []);

  const handleOAuthCallback = async (code, state) => {
    try {
      setConnecting('processing');
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + window.location.pathname;
      
      await api.processGoogleCallback(code, state, redirectUri);
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Reload status
      await loadStatus();
      setConnecting(null);
    } catch (err) {
      console.error('OAuth callback error:', err);
      setError('Error procesando autenticación de Google');
      setConnecting(null);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handleConnect = async (type) => {
    try {
      setConnecting(type);
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUri = window.location.origin + window.location.pathname;
      
      const endpoint = type === 'user' ? api.startGoogleUserAuth : api.startGoogleCompanyAuth;
      const response = await endpoint(currentUser.id, redirectUri);
      
      // Redirect to Google
      window.location.href = response.data.auth_url;
    } catch (err) {
      console.error('Error starting auth:', err);
      setError('Error iniciando autenticación');
      setConnecting(null);
    }
  };

  const handleDisconnect = async (type) => {
    if (!window.confirm(`¿Seguro que deseas desconectar ${type === 'user' ? 'tu cuenta personal' : 'la cuenta de empresa'}?`)) {
      return;
    }
    
    try {
      await api.disconnectGoogle(type, currentUser.id);
      await loadStatus();
    } catch (err) {
      console.error('Error disconnecting:', err);
      setError('Error desconectando cuenta');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="animate-spin mr-2" />
        <span>Cargando configuración de Google...</span>
      </div>
    );
  }

  if (!googleConfig?.configured) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configurar Google OAuth
          </CardTitle>
          <CardDescription>
            Configura las credenciales de OAuth para conectar con Google
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Pasos para obtener las credenciales:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                <li>Ve a <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                <li>Crea un proyecto nuevo o selecciona uno existente</li>
                <li>Ve a "Credenciales" → "Crear credenciales" → "ID de cliente OAuth"</li>
                <li>Tipo: "Aplicación web"</li>
                <li>Agrega URI de redirección: <code className="bg-blue-100 px-1 rounded">{window.location.origin}/api/google/callback</code></li>
                <li>Copia el Client ID y Client Secret</li>
              </ol>
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
              <Input
                value={configForm.client_id}
                onChange={(e) => setConfigForm({ ...configForm, client_id: e.target.value })}
                placeholder="123456789-xxxxxxx.apps.googleusercontent.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={configForm.client_secret}
                  onChange={(e) => setConfigForm({ ...configForm, client_secret: e.target.value })}
                  placeholder="GOCSPX-xxxxxxxxxx"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              onClick={async () => {
                if (!configForm.client_id || !configForm.client_secret) {
                  alert('Por favor completa ambos campos');
                  return;
                }
                setSavingConfig(true);
                try {
                  await api.saveGoogleConfig(configForm);
                  await loadStatus();
                  setShowConfigForm(false);
                  setConfigForm({ client_id: '', client_secret: '' });
                } catch (err) {
                  console.error('Error saving config:', err);
                  alert('Error al guardar configuración: ' + (err.response?.data?.detail || err.message));
                } finally {
                  setSavingConfig(false);
                }
              }}
              disabled={savingConfig || !configForm.client_id || !configForm.client_secret}
              className="w-full bg-[#D4A843] hover:bg-[#c49a3a]"
            >
              {savingConfig ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  Guardar Credenciales
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const userConn = connectionStatus?.user_connection || {};
  const companyConn = connectionStatus?.company_connection || {};

  return (
    <div className="space-y-6">
      {error && (
        <Alert className="bg-red-50 border-red-200">
          <XCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {/* Personal Google Connection - Gmail & Calendar */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-red-500" />
                <Calendar className="h-5 w-5 text-blue-500" />
                Gmail y Calendar Personal
              </CardTitle>
              <CardDescription>
                Conecta tu cuenta de Google para enviar emails y gestionar tu calendario desde el CRM
              </CardDescription>
            </div>
            {userConn.connected && (
              <Badge className="bg-green-100 text-green-800">
                <CheckCircle className="h-3 w-3 mr-1" />
                Conectado
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {userConn.connected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-yellow-500 rounded-full flex items-center justify-center text-white font-bold">
                    {userConn.google_email?.[0]?.toUpperCase() || 'G'}
                  </div>
                  <div>
                    <p className="font-medium">{userConn.google_email}</p>
                    <p className="text-xs text-gray-500">
                      Servicios: {userConn.services?.join(', ')}
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleDisconnect('user')}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Unlink className="h-4 w-4 mr-1" />
                  Desconectar
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 border rounded-lg">
                  <Mail className="h-4 w-4 text-red-500 mb-1" />
                  <p className="font-medium">Gmail</p>
                  <p className="text-xs text-gray-500">Enviar y leer emails</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <Calendar className="h-4 w-4 text-blue-500 mb-1" />
                  <p className="font-medium">Calendar</p>
                  <p className="text-xs text-gray-500">Crear y ver eventos</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Mail className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 mb-4">
                Conecta tu cuenta de Google para habilitar email y calendario
              </p>
              <Button 
                onClick={() => handleConnect('user')}
                disabled={connecting === 'user'}
                className="bg-[#4285F4] hover:bg-[#3367D6]"
              >
                {connecting === 'user' ? (
                  <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Conectando...</>
                ) : (
                  <><ExternalLink className="mr-2 h-4 w-4" /> Conectar con Google</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Google Connection - Drive, Sheets, Docs (Admin only) */}
      {isAdmin && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5 text-yellow-500" />
                  <FileSpreadsheet className="h-5 w-5 text-green-500" />
                  <FileText className="h-5 w-5 text-blue-500" />
                  Drive, Sheets y Docs (Empresa)
                </CardTitle>
                <CardDescription>
                  Cuenta compartida para almacenamiento, hojas de cálculo y documentos de la empresa
                </CardDescription>
              </div>
              {companyConn.connected && (
                <Badge className="bg-green-100 text-green-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Conectado
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {companyConn.connected ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-green-500 rounded-full flex items-center justify-center text-white font-bold">
                      {companyConn.google_email?.[0]?.toUpperCase() || 'E'}
                    </div>
                    <div>
                      <p className="font-medium">{companyConn.google_email}</p>
                      <p className="text-xs text-gray-500">
                        Cuenta de empresa • Servicios: {companyConn.services?.join(', ')}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleDisconnect('company')}
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Unlink className="h-4 w-4 mr-1" />
                    Desconectar
                  </Button>
                </div>
                
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="p-3 border rounded-lg">
                    <FolderOpen className="h-4 w-4 text-yellow-500 mb-1" />
                    <p className="font-medium">Drive</p>
                    <p className="text-xs text-gray-500">Archivos y carpetas</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <FileSpreadsheet className="h-4 w-4 text-green-500 mb-1" />
                    <p className="font-medium">Sheets</p>
                    <p className="text-xs text-gray-500">Hojas de cálculo</p>
                  </div>
                  <div className="p-3 border rounded-lg">
                    <FileText className="h-4 w-4 text-blue-500 mb-1" />
                    <p className="font-medium">Docs</p>
                    <p className="text-xs text-gray-500">Documentos</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <FolderOpen className="h-8 w-8 text-gray-400" />
                </div>
                <p className="text-gray-600 mb-2">
                  Conecta la cuenta de Google de la empresa
                </p>
                <p className="text-xs text-gray-500 mb-4">
                  Esta cuenta se usará para almacenar archivos, exportar reportes y crear documentos
                </p>
                <Button 
                  onClick={() => handleConnect('company')}
                  disabled={connecting === 'company'}
                  className="bg-[#4285F4] hover:bg-[#3367D6]"
                >
                  {connecting === 'company' ? (
                    <><Loader2 className="animate-spin mr-2 h-4 w-4" /> Conectando...</>
                  ) : (
                    <><ExternalLink className="mr-2 h-4 w-4" /> Conectar Cuenta de Empresa</>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Refresh button */}
      <div className="flex justify-end">
        <Button variant="outline" onClick={loadStatus} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar Estado
        </Button>
      </div>
    </div>
  );
};

export default GoogleIntegrationPanel;
